import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellRing, Package, Truck, Users, FileText,
  AlertTriangle, CheckCheck, Check, ChevronRight, X,
  CheckSquare, RefreshCw, AlertOctagon, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const TYPE_CONFIG = {
  new_booking:       { icon: Package,       color: 'text-purple-600',  bg: 'bg-purple-100' },
  booking_confirmed: { icon: CheckCheck,    color: 'text-green-600',   bg: 'bg-green-100'  },
  booking_rejected:  { icon: AlertTriangle, color: 'text-red-600',     bg: 'bg-red-100'    },
  trip_in_transit:   { icon: Truck,         color: 'text-blue-600',    bg: 'bg-blue-100'   },
  trip_completed:    { icon: Check,         color: 'text-green-600',   bg: 'bg-green-100'  },
  docket_delivered:  { icon: FileText,      color: 'text-cyan-600',    bg: 'bg-cyan-100'   },
  new_customer:      { icon: Users,         color: 'text-orange-600',  bg: 'bg-orange-100' },
  task_assigned:     { icon: CheckSquare,   color: 'text-blue-600',    bg: 'bg-blue-100'   },
  task_reallocated:  { icon: RefreshCw,     color: 'text-amber-600',   bg: 'bg-amber-100'  },
  task_overdue:      { icon: AlertOctagon,  color: 'text-red-600',     bg: 'bg-red-100'    },
  task_due_today:    { icon: Clock,         color: 'text-orange-600',  bg: 'bg-orange-100' },
  task_review:       { icon: Clock,         color: 'text-amber-600',   bg: 'bg-amber-100'  },
  task_completed:    { icon: CheckCheck,    color: 'text-green-600',   bg: 'bg-green-100'  },
};

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(() => {
    fetch(`${API}/api/notifications/count`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUnreadCount(d.unread || 0); })
      .catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/notifications?limit=15`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setNotifications(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Poll count every 60s
  useEffect(() => {
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => clearInterval(iv);
  }, [fetchCount]);

  // Load notifications on open
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = useCallback((id) => {
    fetch(`${API}/api/notifications/${id}/read`, { method: 'PUT', headers: authHeaders() })
      .then(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      })
      .catch(() => {});
  }, []);

  const markAllRead = () => {
    fetch(`${API}/api/notifications/read-all`, { method: 'PUT', headers: authHeaders() })
      .then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  };

  const handleItemClick = (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.link_path) navigate(n.link_path);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell-wrapper">
      {/* Bell Button */}
      <button
        data-testid="notifications-button"
        onClick={() => setIsOpen(prev => !prev)}
        className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors relative"
        aria-label="Notifications"
      >
        {unreadCount > 0
          ? <BellRing className="w-5 h-5 text-blue-600" />
          : <Bell className="w-5 h-5 text-gray-600" />
        }
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-12 w-[380px] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-700" />
              <span className="font-semibold text-gray-800 font-ibm text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-ibm font-medium flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600 ml-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[420px] overflow-y-auto" data-testid="notification-list">
            {loading && (
              <div className="text-center py-6 text-gray-400 font-ibm text-sm">Loading...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="text-center py-10">
                <Bell className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 font-ibm text-sm">No notifications yet</p>
                <p className="text-gray-300 font-ibm text-xs mt-1">
                  Booking, trip & docket events will appear here
                </p>
              </div>
            )}

            {!loading && notifications.map((n) => {
              const cfg = TYPE_CONFIG[n.notification_type] || { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-100' };
              const Icon = cfg.icon;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => handleItemClick(n)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-ibm text-gray-800 truncate ${!n.is_read ? 'font-semibold' : 'font-medium'}`}>
                        {n.title}
                      </p>
                      {!n.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    {n.message && (
                      <p className="text-xs text-gray-500 font-ibm truncate mt-0.5">{n.message}</p>
                    )}
                    <p className="text-xs text-gray-400 font-ibm mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {n.link_path && (
                    <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <button
              onClick={() => { navigate('/booking'); setIsOpen(false); }}
              className="text-xs text-blue-600 hover:text-blue-700 font-ibm font-medium"
            >
              View booking requests
            </button>
            <button
              onClick={() => { navigate('/fleet/trips'); setIsOpen(false); }}
              className="text-xs text-blue-600 hover:text-blue-700 font-ibm font-medium"
            >
              View trips
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
